import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getCourse, getCourseTees, getCourseLeaderboard, getCourseLeaderboardMonthly, postScore, verifyScore, reportScore, getScoreVerification, getFriendsCourseLeaderboard, getFriends } from '../api';
import { useAuth } from '../context/AuthContext';
import {
  Spinner, LoadingPage, RankBadge, ScoreBadge, Avatar,
  PageHeader, EmptyState, ErrorMsg, VerifiedBadge, formatDate
} from '../components/UI';

// ── Small tee color dot ───────────────────────────────────────────────────────
function TeeDot({ color, size = 'sm' }) {
  const dim = size === 'lg' ? 'w-5 h-5' : 'w-3 h-3';
  const bg  = color?.toLowerCase() === 'white' ? '#f0f0f0' : color?.toLowerCase();
  return (
    <span
      className={`${dim} rounded-full border border-gray-300 inline-block flex-shrink-0`}
      style={{ backgroundColor: bg }}
    />
  );
}

// ── Tee attribute pill shown on each leaderboard row ─────────────────────────
function TeePill({ color, yardage, gender }) {
  return (
    <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-500 text-xs rounded-full px-2 py-0.5 flex-shrink-0">
      <TeeDot color={color} />
      <span className="capitalize">{color}</span>
      <span className="text-gray-300">·</span>
      <span>{yardage?.toLocaleString()} yds</span>
      <span className="text-gray-300">·</span>
      <span>{gender === 'male' ? 'M' : 'F'}</span>
    </span>
  );
}

// ── Verification detail modal ─────────────────────────────────────────────────
function VerificationModal({ scoreId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    getScoreVerification(scoreId).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [scoreId]);
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4">
      <div className="card w-full max-w-sm p-6 fade-in">
        <h3 className="font-serif font-bold text-xl text-green-900 mb-4">Score Verification</h3>
        {loading ? <div className="flex justify-center py-4"><Spinner /></div>
          : data ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3 bg-green-50 rounded-lg p-3">
                <span className="text-2xl">✅</span>
                <div>
                  <p className="text-sm font-semibold text-green-900">Verified by {data.users?.username}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{formatDate(data.verified_at)}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500">This player vouched that this is a genuine round of golf.</p>
            </div>
          ) : <p className="text-sm text-gray-500">No verification details found.</p>
        }
        <button onClick={onClose} className="btn-secondary w-full mt-5">Close</button>
      </div>
    </div>
  );
}

// ── Verify modal ──────────────────────────────────────────────────────────────
function VerifyModal({ entry, onClose, onVerified }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const handleVerify = async () => {
    setLoading(true); setError('');
    try { await verifyScore(entry.score_id); onVerified(); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4">
      <div className="card w-full max-w-sm p-6 fade-in">
        <h3 className="font-serif font-bold text-xl text-green-900 mb-2">Verify This Score?</h3>
        <p className="text-sm text-gray-600 mb-1">
          You are vouching that <span className="font-semibold text-green-800">{entry.username}</span>'s
          score of <span className="font-semibold">{entry.total_score}</span> (
          <span className={entry.over_under_par < 0 ? 'text-green-700 font-semibold' : entry.over_under_par > 0 ? 'text-red-600 font-semibold' : 'font-semibold'}>
            {entry.over_under_par === 0 ? 'E' : entry.over_under_par > 0 ? `+${entry.over_under_par}` : entry.over_under_par}
          </span>) is a legitimate round of golf.
        </p>
        <p className="text-xs text-gray-400 mb-5">You can only verify each score once.</p>
        <ErrorMsg message={error} />
        <div className="flex gap-3 mt-2">
          <button onClick={onClose} className="btn-secondary flex-1" disabled={loading}>Cancel</button>
          <button
            onClick={handleVerify}
            className="flex-1 bg-green-700 text-white font-medium py-2.5 px-5 rounded-lg hover:bg-green-600 transition-all text-sm flex items-center justify-center gap-2"
            disabled={loading}
          >
            {loading ? <Spinner size="sm" /> : '✅ Yes, I Verify This'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Report modal ──────────────────────────────────────────────────────────────
function ReportModal({ entry, onClose, onReported }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const handleReport = async () => {
    setLoading(true); setError('');
    try { await reportScore(entry.score_id); onReported(); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4">
      <div className="card w-full max-w-sm p-6 fade-in">
        <h3 className="font-serif font-bold text-xl text-green-900 mb-2">Report This Score?</h3>
        <p className="text-sm text-gray-600 mb-1">
          Flagging <span className="font-semibold">{entry.username}</span>'s score of{' '}
          <span className="font-semibold">{entry.total_score}</span> as potentially illegitimate.
        </p>
        <p className="text-xs text-gray-400 mb-1">Reports are anonymous. 10+ reports triggers an admin review.</p>
        <p className="text-xs text-red-500 mb-5">Only report scores you genuinely believe are fraudulent.</p>
        <ErrorMsg message={error} />
        <div className="flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1" disabled={loading}>Cancel</button>
          <button
            onClick={handleReport}
            className="flex-1 bg-red-600 text-white font-medium py-2.5 px-5 rounded-lg hover:bg-red-700 transition-all text-sm flex items-center justify-center gap-2"
            disabled={loading}
          >
            {loading ? <Spinner size="sm" /> : '🚩 Report Score'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Per-row action menu (⋯ on mobile, inline on desktop) ─────────────────────
function ScoreActions({ entry, userId, course, onRefresh }) {
  const [open,                   setOpen]                  = useState(false);
  const [showVerify,             setShowVerify]            = useState(false);
  const [showReport,             setShowReport]            = useState(false);
  const [showVerificationDetail, setShowVerificationDetail]= useState(false);
  const [justVerified,           setJustVerified]          = useState(false);
  const [justReported,           setJustReported]          = useState(false);
  const [copied,                 setCopied]                = useState(false);
  const menuRef = useRef(null);
  const isOwn      = userId === entry.user_id;
  const iPostedThis = entry.posted_by_friend === userId;

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const scoreDisplay = entry.over_under_par === 0 ? 'E'
    : entry.over_under_par > 0 ? `+${entry.over_under_par}` : `${entry.over_under_par}`;
  const shareText = `🏌️ ${entry.username} shot ${entry.total_score} (${scoreDisplay}) on the ${entry.tee_color} tees at ${course?.name} — ranked #${entry.place}!\nCheck the leaderboard on GolfBoard Scores: https://golfboardscores.com/leaderboard/course/${course?.id}`;

  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(shareText); }
    catch {
      const el = document.createElement('textarea');
      el.value = shareText; document.body.appendChild(el); el.select();
      document.execCommand('copy'); document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => { setCopied(false); setOpen(false); }, 1500);
  };

  return (
    <>
      {/* Desktop: inline buttons */}
      <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0">
        <button
          onClick={handleCopy}
          className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-all duration-200 ${
            copied ? 'bg-green-50 border-green-300 text-green-700 font-semibold' : 'bg-white border-gray-200 text-gray-400 hover:border-green-400 hover:text-green-700'
          }`}
        >
          {copied ? <>✓ Copied!</> : <>🔗 Share</>}
        </button>
        {entry.is_verified ? (
          <button onClick={() => setShowVerificationDetail(true)}
            className="flex items-center gap-1 text-xs bg-green-100 text-green-800 font-semibold rounded-full px-2 py-1 hover:bg-green-200 transition-colors">
            ✓ Verified
          </button>
        ) : !isOwn && !iPostedThis && !justVerified && (
          <button onClick={() => setShowVerify(true)}
            className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-400 hover:border-green-400 hover:text-green-700 transition-all bg-white">
            Verify
          </button>
        )}
        {!isOwn && !justReported && (
          <button onClick={() => setShowReport(true)}
            className="text-xs px-2 py-1.5 rounded-lg border border-gray-200 text-gray-300 hover:border-red-300 hover:text-red-500 transition-all bg-white"
            title="Report score">🚩</button>
        )}
        {justReported && <span className="text-xs text-red-400">Reported</span>}
      </div>

      {/* Mobile: ⋯ dropdown */}
      <div className="sm:hidden relative flex-shrink-0" ref={menuRef}>
        <button
          onClick={() => setOpen(o => !o)}
          className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-green-400 hover:text-green-700 bg-white transition-all font-bold text-base leading-none"
          aria-label="Score actions"
        >···</button>

        {open && (
          <div className="absolute right-0 top-9 z-30 w-44 bg-white border border-amber-100 rounded-xl shadow-lg py-1 fade-in">
            <button onClick={handleCopy}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50 transition-colors">
              <span>{copied ? '✓' : '🔗'}</span>{copied ? 'Copied!' : 'Share Score'}
            </button>
            {entry.is_verified ? (
              <button onClick={() => { setShowVerificationDetail(true); setOpen(false); }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-green-700 hover:bg-green-50 transition-colors">
                <span>✅</span> View Verification
              </button>
            ) : !isOwn && !iPostedThis && !justVerified && (
              <button onClick={() => { setShowVerify(true); setOpen(false); }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50 transition-colors">
                <span>✓</span> Verify Score
              </button>
            )}
            {!isOwn && !justReported && (
              <button onClick={() => { setShowReport(true); setOpen(false); }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors">
                <span>🚩</span> Report Score
              </button>
            )}
            {justReported && <div className="px-4 py-2.5 text-xs text-red-400">Reported</div>}
          </div>
        )}
      </div>

      {showVerify && (
        <VerifyModal entry={entry} onClose={() => setShowVerify(false)}
          onVerified={() => { setShowVerify(false); setJustVerified(true); onRefresh(); }} />
      )}
      {showReport && (
        <ReportModal entry={entry} onClose={() => setShowReport(false)}
          onReported={() => { setShowReport(false); setJustReported(true); }} />
      )}
      {showVerificationDetail && (
        <VerificationModal scoreId={entry.score_id} onClose={() => setShowVerificationDetail(false)} />
      )}
    </>
  );
}

// ── Add Score Modal ───────────────────────────────────────────────────────────
function AddScoreModal({ course, tees, userId, onClose, onSuccess }) {
  const maleTees   = tees.filter(t => t.gender === 'male').sort((a, b) => b.yardage - a.yardage);
  const femaleTees = tees.filter(t => t.gender === 'female').sort((a, b) => b.yardage - a.yardage);
  const today      = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  // My own score entry
  const [myTeeId,    setMyTeeId]    = useState('');
  const [myScore,    setMyScore]    = useState('');

  // Friends: up to 3 slots, each { friendshipId, user, teeId, score }
  const [friends,        setFriends]        = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(false);
  // friendEntries: array of { key, selectedFriend, teeId, score }
  const [friendEntries,  setFriendEntries]  = useState([]);

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => {
    setFriendsLoading(true);
    getFriends()
      .then(data => setFriends(data?.friends || []))
      .catch(() => setFriends([]))
      .finally(() => setFriendsLoading(false));
  }, []);

  const addFriendEntry = () => {
    if (friendEntries.length >= 3) return;
    setFriendEntries(prev => [...prev, { key: Date.now(), selectedFriend: null, teeId: '', score: '' }]);
  };

  const removeFriendEntry = (key) => {
    setFriendEntries(prev => prev.filter(e => e.key !== key));
  };

  const updateEntry = (key, field, value) => {
    setFriendEntries(prev => prev.map(e => e.key === key ? { ...e, [field]: value } : e));
  };

  // Friends already picked in other slots (so you can't pick the same friend twice)
  const pickedFriendIds = new Set(friendEntries.map(e => e.selectedFriend?.user?.id).filter(Boolean));

  const myTee    = tees.find(t => t.id === myTeeId);
  const myOverUnder = myScore !== '' && myTee ? parseInt(myScore) - myTee.par : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate my score
    if (!myTeeId)           { setError('Please select your tee.');  return; }
    if (!myScore)           { setError('Please enter your score.'); return; }
    const myScoreInt = parseInt(myScore);
    if (isNaN(myScoreInt))  { setError('Please enter a valid score.'); return; }

    // Validate friend entries
    for (const entry of friendEntries) {
      if (!entry.selectedFriend) { setError('Please select a friend for each added row, or remove empty rows.'); return; }
      if (!entry.teeId)          { setError(`Please select a tee for ${entry.selectedFriend.user.username}.`); return; }
      if (!entry.score)          { setError(`Please enter a score for ${entry.selectedFriend.user.username}.`); return; }
      if (isNaN(parseInt(entry.score))) { setError(`Invalid score for ${entry.selectedFriend.user.username}.`); return; }
    }

    setLoading(true);
    try {
      // Build all submissions — mine first, then friends
      const submissions = [
        { tee_id: myTeeId, total_score: myScoreInt, over_under_par: myScoreInt - myTee.par },
        ...friendEntries.map(entry => {
          const friendTee  = tees.find(t => t.id === entry.teeId);
          const friendScore = parseInt(entry.score);
          return {
            tee_id:       entry.teeId,
            total_score:  friendScore,
            over_under_par: friendScore - friendTee.par,
            for_user_id:  entry.selectedFriend.user.id
          };
        })
      ];

      // Post all in parallel
      await Promise.all(submissions.map(s => postScore(s)));
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const TeeButton = ({ tee, selectedId, onSelect }) => (
    <button
      type="button"
      onClick={() => onSelect(tee.id)}
      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border text-sm transition-all ${
        selectedId === tee.id
          ? 'border-green-600 bg-green-50 text-green-900'
          : 'border-gray-200 hover:border-green-300 text-gray-700'
      }`}
    >
      <TeeDot color={tee.color} size="lg" />
      <span className="font-medium capitalize flex-1 text-left">{tee.color}</span>
      <span className="text-xs text-gray-400">Par {tee.par} · {tee.yardage?.toLocaleString()} yds</span>
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4">
      <div className="card w-full max-w-sm p-6 fade-in max-h-[90vh] overflow-y-auto">
        <h3 className="font-serif font-bold text-xl text-green-900 mb-1">Add Score</h3>
        <p className="text-sm text-gray-500 mb-5">{course?.name} · {today}</p>

        <form onSubmit={handleSubmit} className="space-y-5">

          {/* ── My score ── */}
          <div className="bg-green-50 border border-green-100 rounded-xl p-4">
            <p className="text-xs font-semibold text-green-800 uppercase tracking-wide mb-3">Your Round</p>
            <div className="mb-3">
              {maleTees.length > 0 && (
                <div className="mb-2">
                  <p className="text-xs text-gray-400 uppercase tracking-widest mb-1.5">Men's</p>
                  <div className="space-y-1.5">
                    {maleTees.map(t => <TeeButton key={t.id} tee={t} selectedId={myTeeId} onSelect={setMyTeeId} />)}
                  </div>
                </div>
              )}
              {femaleTees.length > 0 && (
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-widest mb-1.5">Women's</p>
                  <div className="space-y-1.5">
                    {femaleTees.map(t => <TeeButton key={t.id} tee={t} selectedId={myTeeId} onSelect={setMyTeeId} />)}
                  </div>
                </div>
              )}
            </div>
            <input
              type="number"
              className="input-field text-center text-xl font-serif font-bold"
              placeholder={myTee?.par || 'Score'}
              min={18} max={300}
              value={myScore}
              onChange={e => setMyScore(e.target.value)}
              required
            />
            {myOverUnder !== null && (
              <div className="mt-2 flex justify-between text-sm">
                <span className="text-gray-500">Over/Under</span>
                <ScoreBadge overUnder={myOverUnder} />
              </div>
            )}
          </div>

          {/* ── Friend score entries ── */}
          {friendEntries.map((entry, idx) => {
            const friendTee = tees.find(t => t.id === entry.teeId);
            const friendOU  = entry.score !== '' && friendTee ? parseInt(entry.score) - friendTee.par : null;
            const availableFriends = friends.filter(f => !pickedFriendIds.has(f.user.id) || f.user.id === entry.selectedFriend?.user?.id);
            return (
              <div key={entry.key} className="bg-amber-50 border border-amber-100 rounded-xl p-4 relative">
                <button
                  type="button"
                  onClick={() => removeFriendEntry(entry.key)}
                  className="absolute top-3 right-3 text-gray-400 hover:text-red-500 text-sm transition-colors"
                >✕</button>
                <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-3">Friend {idx + 1}</p>

                {/* Friend picker */}
                {friendsLoading ? (
                  <div className="flex justify-center py-2"><Spinner size="sm" /></div>
                ) : friends.length === 0 ? (
                  <p className="text-xs text-gray-400 mb-3">No accepted friends yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {availableFriends.map(f => (
                      <button
                        key={f.friendship_id}
                        type="button"
                        onClick={() => updateEntry(entry.key, 'selectedFriend', f)}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-all ${
                          entry.selectedFriend?.friendship_id === f.friendship_id
                            ? 'border-amber-500 bg-amber-100 text-amber-900'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-amber-400'
                        }`}
                      >
                        <div className="w-4 h-4 rounded-full bg-green-800 text-amber-100 flex items-center justify-center text-xs font-semibold flex-shrink-0" style={{fontSize:'0.5rem'}}>
                          {f.user.username?.slice(0, 2).toUpperCase()}
                        </div>
                        {f.user.username}
                      </button>
                    ))}
                  </div>
                )}

                {/* Tee picker */}
                {entry.selectedFriend && (
                  <>
                    <div className="mb-3">
                      {maleTees.length > 0 && (
                        <div className="mb-2">
                          <p className="text-xs text-gray-400 uppercase tracking-widest mb-1.5">Men's</p>
                          <div className="space-y-1.5">
                            {maleTees.map(t => <TeeButton key={t.id} tee={t} selectedId={entry.teeId} onSelect={v => updateEntry(entry.key, 'teeId', v)} />)}
                          </div>
                        </div>
                      )}
                      {femaleTees.length > 0 && (
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-widest mb-1.5">Women's</p>
                          <div className="space-y-1.5">
                            {femaleTees.map(t => <TeeButton key={t.id} tee={t} selectedId={entry.teeId} onSelect={v => updateEntry(entry.key, 'teeId', v)} />)}
                          </div>
                        </div>
                      )}
                    </div>
                    <input
                      type="number"
                      className="input-field text-center text-xl font-serif font-bold"
                      placeholder={friendTee?.par || 'Score'}
                      min={18} max={300}
                      value={entry.score}
                      onChange={e => updateEntry(entry.key, 'score', e.target.value)}
                    />
                    {friendOU !== null && (
                      <div className="mt-2 flex justify-between text-sm">
                        <span className="text-gray-500">Over/Under</span>
                        <ScoreBadge overUnder={friendOU} />
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}

          {/* Add friend row button */}
          {friendEntries.length < 3 && friends.length > 0 && (
            <button
              type="button"
              onClick={addFriendEntry}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-amber-300 text-amber-700 hover:bg-amber-50 text-sm font-medium transition-colors"
            >
              + Add a Friend's Score
            </button>
          )}

          <ErrorMsg message={error} />
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button
              type="submit"
              className="btn-primary flex-1 flex items-center justify-center gap-2"
              disabled={loading || !myTeeId || !myScore}
            >
              {loading ? <Spinner size="sm" /> : `Submit ${friendEntries.length > 0 ? `${friendEntries.length + 1} Scores` : 'Score'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Leaderboard row ───────────────────────────────────────────────────────────
function LeaderboardRow({ entry, userId, course, onRefresh }) {
  const isPendingFriendPost = entry.friend_post_status === 'pending_player';
  const isMyPendingPost     = isPendingFriendPost && entry.user_id === userId;

  return (
    <div className={`card p-3 flex items-center gap-3 fade-in ${isPendingFriendPost ? 'opacity-80 border-amber-200' : ''}`}>
      <RankBadge rank={entry.place} />

      <Link
        to={`/profile/${entry.user_id}`}
        className="flex items-center gap-2 flex-1 min-w-0 hover:opacity-80 transition-opacity"
      >
        <Avatar username={entry.username} url={entry.avatar_url} size="sm" />
        <div className="min-w-0">
          <div className="font-medium text-green-900 truncate text-sm leading-tight">{entry.username}</div>
          <TeePill color={entry.tee_color} yardage={entry.tee_yardage} gender={entry.tee_gender} />
          <div className="text-xs text-gray-400 mt-0.5">{formatDate(entry.date_posted)}</div>
          {isPendingFriendPost && (
            <div className="inline-flex items-center gap-1 mt-0.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium rounded-full px-2 py-0.5">
              📤 {isMyPendingPost ? 'Pending your review' : 'Posted by friend · pending'}
            </div>
          )}
        </div>
      </Link>

      <div className="text-right flex-shrink-0">
        <div className="font-serif font-bold text-lg text-green-900 leading-tight">{entry.total_score}</div>
        <div className="text-xs"><ScoreBadge overUnder={entry.over_under_par} /></div>
      </div>

      <ScoreActions entry={entry} userId={userId} course={course} onRefresh={onRefresh} />
    </div>
  );
}

// ── Monthly leaderboard tab ───────────────────────────────────────────────────
function MonthlyLeaderboard({ courseId, course, userId, onRefresh }) {
  const now = new Date();
  const [year,        setYear]        = useState(now.getFullYear());
  const [month,       setMonth]       = useState(now.getMonth() + 1);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');

  const MONTHS = ['January','February','March','April','May','June',
                  'July','August','September','October','November','December'];

  useEffect(() => {
    setLoading(true); setError('');
    getCourseLeaderboardMonthly(courseId, year, month)
      .then(res => setLeaderboard(res.data || []))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [courseId, year, month]);

  const yearOptions = [];
  for (let y = now.getFullYear(); y >= 2024; y--) yearOptions.push(y);

  return (
    <div>
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <select className="input-field py-1.5 text-sm w-auto" value={month} onChange={e => setMonth(Number(e.target.value))}>
          {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
        </select>
        <select className="input-field py-1.5 text-sm w-auto" value={year} onChange={e => setYear(Number(e.target.value))}>
          {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="text-xs text-gray-400">Showing top scores for {MONTHS[month - 1]} {year}</span>
      </div>

      {error && <div className="text-sm text-red-500 mb-4">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-10"><Spinner size="lg" /></div>
      ) : leaderboard.length === 0 ? (
        <EmptyState icon="📅" title="No scores this month"
          message={`No scores were posted at ${course?.name} in ${MONTHS[month - 1]} ${year}.`} />
      ) : (
        <div className="space-y-2">
          {leaderboard.map((entry, idx) => (
            <LeaderboardRow key={entry.score_id || idx} entry={entry} userId={userId} course={course} onRefresh={onRefresh} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Friends leaderboard tab ───────────────────────────────────────────────────
function FriendsLeaderboard({ courseId, course, userId, onRefresh }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');

  useEffect(() => {
    setLoading(true); setError('');
    getFriendsCourseLeaderboard(courseId)
      .then(res => setLeaderboard(res.leaderboard || []))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [courseId]);

  if (loading) return <div className="flex justify-center py-10"><Spinner size="lg" /></div>;
  if (error)   return <div className="text-sm text-red-500 py-4">{error}</div>;

  return leaderboard.length === 0 ? (
    <EmptyState
      icon="🤝"
      title="No friend scores yet"
      message="Add friends from their profile page to see how you compare on this course."
    />
  ) : (
    <div className="space-y-2">
      {leaderboard.map((entry, idx) => (
        <LeaderboardRow key={entry.score_id || idx} entry={entry} userId={userId} course={course} onRefresh={onRefresh} />
      ))}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function CourseLeaderboardPage() {
  const { courseId } = useParams();
  const { user }     = useAuth();

  const [course,      setCourse]      = useState(null);
  const [tees,        setTees]        = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showModal,   setShowModal]   = useState(false);
  const [activeTab,   setActiveTab]   = useState('alltime');

  const fetchData = async () => {
    try {
      const [courseData, teesData, lbData] = await Promise.all([
        getCourse(courseId),
        getCourseTees(courseId),
        getCourseLeaderboard(courseId),
      ]);
      setCourse(courseData);
      setTees(teesData);
      setLeaderboard(lbData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [courseId]);

  if (loading) return <LoadingPage />;
  if (!course)  return <div className="page-container"><p>Course not found.</p></div>;

  return (
    <div className="page-container">
      <PageHeader
        back
        title={course.name}
        subtitle={[course.city, course.state, course.country].filter(Boolean).join(', ')}
      />

      {/* Course summary + add score CTA */}
      <div className="card p-4 mb-5 flex items-center justify-between gap-4">
        <div>
          <p className="text-xs text-gray-400 mb-1.5">All tees compete together · same score on same tee = tied</p>
          <div className="flex flex-wrap gap-1.5">
            {[...tees].sort((a, b) => b.yardage - a.yardage).map(t => (
              <TeePill key={t.id} color={t.color} yardage={t.yardage} gender={t.gender} />
            ))}
          </div>
        </div>
        <button onClick={() => setShowModal(true)} className="btn-gold flex-shrink-0">+ Score</button>
      </div>

      {/* All-Time / Monthly / Friends tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1">
        {[['alltime','🏆 All-Time'],['monthly','📅 Monthly'],['friends','🤝 Friends']].map(([tab, label]) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 text-sm font-medium py-2 rounded-lg transition-all ${
              activeTab === tab ? 'bg-white text-green-900 shadow-sm' : 'text-gray-500 hover:text-green-800'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* All-Time */}
      {activeTab === 'alltime' && (
        <>
          <div className="flex items-center gap-2 mb-4 text-xs text-gray-400 px-1">
            <span className="flex items-center gap-1">
              <span className="bg-green-100 text-green-800 font-semibold rounded-full px-1.5 py-0.5">✓ Verified</span>
              = another player vouched for this score
            </span>
          </div>

          {leaderboard.length === 0 ? (
            <EmptyState
              icon="🏌️"
              title="No scores yet"
              message="Be the first to post a score at this course!"
              action={<button onClick={() => setShowModal(true)} className="btn-gold">+ Add Your Score</button>}
            />
          ) : (
            <div className="space-y-2">
              {leaderboard.map(entry => (
                <LeaderboardRow key={entry.score_id} entry={entry} userId={user?.id} course={course} onRefresh={fetchData} />
              ))}
            </div>
          )}

          {leaderboard.length > 0 && (
            <p className="text-center text-xs text-gray-400 mt-4">
              {leaderboard[leaderboard.length - 1]?.total_scores_on_course || leaderboard.length} total score{leaderboard.length !== 1 ? 's' : ''}
            </p>
          )}
        </>
      )}

      {/* Monthly */}
      {activeTab === 'monthly' && (
        <MonthlyLeaderboard courseId={courseId} course={course} userId={user?.id} onRefresh={fetchData} />
      )}

      {/* Friends */}
      {activeTab === 'friends' && (
        <FriendsLeaderboard courseId={courseId} course={course} userId={user?.id} onRefresh={fetchData} />
      )}

      {showModal && (
        <AddScoreModal
          course={course}
          tees={tees}
          userId={user?.id}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); fetchData(); }}
        />
      )}
    </div>
  );
}